import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";
import { GetAllCompanyDto } from "@/types/api/api-types";
import {
  DEFAULT_OPENING_HOURS,
  OpeningHours,
  PickUpLocationFormData,
  pickUpLocationSchema,
} from "@/types/forms/auth/registerFormData.form";
import { useDeletePickUpLocation, usePatchPickUpLocation } from "@/api/pickUpLocation.api";
import { PickUpLocationElem } from "./pickUpLocationElem";
import { PickUpLocationForm } from "@/components/pickUpLocation/PickUpLocationForm";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";

type GeneralInfoModalPickupProps = {
  closeModal: () => void;
  company: GetAllCompanyDto;
};

export const GeneralInfoModalPickup = ({ closeModal, company }: GeneralInfoModalPickupProps) => {
  const { t } = useTranslation(["boClients", "common"]);
  const [selectedPickUpLocation, setSelectedPickUpLocation] = useState<string | null>(null);
  const { mutateAsync: patchPickUpLocation, isPending } = usePatchPickUpLocation();
  const { mutateAsync: deletePickUpLocation } = useDeletePickUpLocation();

  const selectedPickUpLocationData = company?.pickUpLocations.find(
    (el) => el.id === selectedPickUpLocation,
  );

  const selectedPickUpLocationName = selectedPickUpLocationData?.name;
  const isSelectedPickUpLocationDefault = selectedPickUpLocationData?.isDefault;

  const pickUpLocationForm = useForm<PickUpLocationFormData>({
    resolver: zodResolver(pickUpLocationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      street: "",
      postalCode: "",
      city: "",
      contactNumber: "",
      contactNumber2: "",
      openingHours: DEFAULT_OPENING_HOURS,
      isDefault: false,
    },
  });

  const onSubmit: SubmitHandler<PickUpLocationFormData> = async (data) => {
    if (!selectedPickUpLocation) return;
    const ret = await patchPickUpLocation({
      data,
      id: selectedPickUpLocation,
    });
    if (ret?.status === 200) {
      closeModal();
    }
  };

  const handleDeletePickUpLocation = (id: string) => {
    deletePickUpLocation(id);
  };

  const handleUpdatePickUpLocation = (id: string) => {
    const pickUpLocation = company!.pickUpLocations.find((el) => el.id === id);
    if (!pickUpLocation) return;
    pickUpLocationForm.setValue("name", pickUpLocation.name);
    pickUpLocationForm.setValue("street", pickUpLocation.street);
    pickUpLocationForm.setValue("postalCode", pickUpLocation.postalCode);
    pickUpLocationForm.setValue("city", pickUpLocation.city);
    pickUpLocationForm.setValue("contactNumber", pickUpLocation.contactNumber);
    pickUpLocationForm.setValue("contactNumber2", pickUpLocation.contactNumber2 || "");
    pickUpLocationForm.setValue("openingHours", pickUpLocation.openingHours as OpeningHours);
    pickUpLocationForm.setValue("isDefault", pickUpLocation.isDefault);
    setSelectedPickUpLocation(id);
  };

  const endUpdate = () => {
    setSelectedPickUpLocation(null);
    pickUpLocationForm.reset();
  };

  if (!selectedPickUpLocation) {
    return (
      <Modal
        open={true}
        onClose={closeModal}
        title={t("profilePickupModal.listTitle")}
        size="md"
        dismissible
      >
        <div className="flex w-full flex-col gap-4">
          <h3 className="text-brand700 text-base font-semibold">
            {t("profilePickupModal.listSubtitle")}
          </h3>
          {company?.pickUpLocations.map((pickupLocation, index) => (
            <PickUpLocationElem
              key={index}
              isDefault={pickupLocation.isDefault}
              name={pickupLocation.name}
              deletePickUpLocation={() => handleDeletePickUpLocation(pickupLocation.id)}
              updatePickUpLocation={() => handleUpdatePickUpLocation(pickupLocation.id)}
            />
          ))}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={true}
      onClose={closeModal}
      title={t("profilePickupModal.editTitle", { name: selectedPickUpLocationName })}
      size="md"
      contentClassName="md:min-w-[480px]"
      footer={
        <div className="flex w-full gap-2">
          <Button type="button" variant="secondary" onClick={endUpdate} className="flex-1">
            {t("common:actions.back")}
          </Button>
          <Button
            type="submit"
            form="edit-pickup-location-form"
            className="flex-1"
            disabled={isPending}
          >
            {t("profilePickupModal.submit")}
          </Button>
        </div>
      }
    >
      <form
        id="edit-pickup-location-form"
        onSubmit={pickUpLocationForm.handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-3"
      >
        <PickUpLocationForm
          control={pickUpLocationForm.control}
          watch={pickUpLocationForm.watch}
          setValue={pickUpLocationForm.setValue}
          errors={pickUpLocationForm.formState.errors}
          showIsDefault={!isSelectedPickUpLocationDefault}
          register={pickUpLocationForm.register}
        />
      </form>
    </Modal>
  );
};
